import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Building2, Home, Euro, Calendar, CheckCircle2, TrendingUp,
  FileText, MapPin, Filter, Search, Send, ExternalLink,
} from 'lucide-react';
import BookSeniorModal from './BookSeniorModal';

// ★ 2026-08-11 (Fase 37) — Tabella appartamenti pubblica + form riserva
// Usata nella pagina /resort/la-mar-residence
// Fetch: GET /api/public/projects/{slug}/units
// Submit: POST /api/public/projects/{slug}/units/{unit_id}/request-info

const fmtEUR = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};

// ★ 2026-08-26 (Fase 55) — Status keys are now translated via i18n. Class names
// remain here; labels are picked in-render via t('resortDetail.unitsTable.badges.*').
const STATUS_STYLES = {
  available: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  reserved:  'bg-amber-100 text-amber-800 border-amber-300',
  sold:      'bg-red-100 text-red-800 border-red-300',
  off_market:'bg-slate-100 text-slate-600 border-slate-300',
};

const STATUS_BADGE_KEYS = {
  available: 'resortDetail.unitsTable.badges.available',
  reserved:  'resortDetail.unitsTable.badges.reserved',
  sold:      'resortDetail.unitsTable.badges.sold',
  off_market:'resortDetail.unitsTable.badges.offMarket',
};

export default function PropertyUnitsTable({ slug }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [counts, setCounts] = useState({});
  const [blockFilter, setBlockFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);

  // ★ 2026-08-14 (Fase 51) — Modal Prenota Zoom con Senior/CEO
  const [bookOpen, setBookOpen] = useState(false);
  const [bookContext, setBookContext] = useState({});

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/public/projects/${slug}/units`)
      .then((r) => {
        setUnits(r.data?.units || []);
        setCounts(r.data?.counts || {});
      })
      .catch(() => {
        setUnits([]);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const blocks = useMemo(() => {
    // ★ 2026-08-14 (Fase 51.5) — Sort naturale per numero (BLOCCO 1..22)
    const set = new Set(units.map((u) => u.block).filter(Boolean));
    return Array.from(set).sort((a, b) => {
      const na = parseInt(String(a).match(/\d+/)?.[0] || '0', 10);
      const nb = parseInt(String(b).match(/\d+/)?.[0] || '0', 10);
      if (na !== nb) return na - nb;
      return String(a).localeCompare(String(b));
    });
  }, [units]);

  const filtered = useMemo(() => {
    return units.filter((u) => {
      if (blockFilter !== 'all' && u.block !== blockFilter) return false;
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!(u.code || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [units, blockFilter, statusFilter, search]);

  const openReserveModal = (unit) => {
    // ★ 2026-08-14 (Fase 51) — L'azione "Info" ora apre il BookSeniorModal
    //   invece del vecchio form "Richiedi info". Chi vuole info sull'unità
    //   fissa direttamente una Zoom col Senior del paese o con il CEO.
    setBookContext({
      unitCode: unit?.code,
      subject: `Info unit\u00e0 ${unit?.code || ''}`.trim(),
      sourceLabel: 'property_units_table',
    });
    setBookOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t('resortDetail.unitsTable.errors.nameEmail', 'Inserisci nome ed email'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/public/projects/${slug}/units/${selectedUnit.id}/request-info`, {
        unit_id: selectedUnit.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setSubmittedOk(true);
      toast.success(t('resortDetail.unitsTable.success', 'Richiesta inviata! Ti contatteremo entro 24h.'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('resortDetail.unitsTable.errors.submit', 'Errore invio richiesta'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2" data-testid="units-table-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-muted-foreground">
          {t('resortDetail.unitsTable.empty', 'Nessun appartamento pubblicato per questo progetto.')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="property-units-table">
      {/* Header con conteggi */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border" data-testid="count-available">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {t('resortDetail.unitsTable.availableCount', '{{count}} disponibili', { count: counts.available || 0 })}
          </Badge>
          {(counts.reserved || 0) > 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 border">
              {t('resortDetail.unitsTable.reservedCount', '{{count}} prenotati', { count: counts.reserved })}
            </Badge>
          )}
          {(counts.sold || 0) > 0 && (
            <Badge className="bg-red-100 text-red-800 border-red-300 border">
              {t('resortDetail.unitsTable.soldCount', '{{count}} non disponibili', { count: counts.sold })}
            </Badge>
          )}
        </div>
      </div>

      {/* Filtri */}
      <Card className="bg-[var(--surface-1)]">
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">{t('resortDetail.unitsTable.filters.block', 'Blocco')}</Label>
            <Select value={blockFilter} onValueChange={setBlockFilter}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-block">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('resortDetail.unitsTable.filters.blockAll', 'Tutti i blocchi')}</SelectItem>
                {blocks.map((b) => (
                  <SelectItem key={b} value={b}>{t('resortDetail.unitsTable.filters.blockOption', 'Blocco {{name}}', { name: b })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">{t('resortDetail.unitsTable.filters.availability', 'Disponibilità')}</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('resortDetail.unitsTable.filters.statusAll', 'Tutti gli stati')}</SelectItem>
                <SelectItem value="available">{t('resortDetail.unitsTable.filters.statusAvailable', 'Solo disponibili')}</SelectItem>
                <SelectItem value="reserved">{t('resortDetail.unitsTable.filters.statusReserved', 'Solo prenotati')}</SelectItem>
                <SelectItem value="sold">{t('resortDetail.unitsTable.filters.statusSold', 'Solo non disponibili')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase text-muted-foreground">{t('resortDetail.unitsTable.filters.searchLabel', 'Cerca codice')}</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('resortDetail.unitsTable.filters.searchPlaceholder', 'es. A-001')}
                className="pl-8 h-9"
                data-testid="filter-search"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card className="overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="units-table">
            <thead className="bg-[var(--surface-2)] border-b border-border">
              <tr className="text-left">
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">{t('resortDetail.unitsTable.cols.code', 'Codice')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground">{t('resortDetail.unitsTable.cols.block', 'Blocco')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">{t('resortDetail.unitsTable.cols.sqm', 'MQ')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right">{t('resortDetail.unitsTable.cols.price', 'Prezzo')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right hidden md:table-cell">{t('resortDetail.unitsTable.cols.pricePerSqm', '€/mq')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-right hidden lg:table-cell">{t('resortDetail.unitsTable.cols.roi', 'ROI/anno')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-center">{t('resortDetail.unitsTable.cols.status', 'Stato')}</th>
                <th className="px-3 py-2.5 text-[10px] uppercase text-muted-foreground text-center">{t('resortDetail.unitsTable.cols.action', 'Azione')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const statusStyle = STATUS_STYLES[u.status] || STATUS_STYLES.available;
                const statusLabel = t(STATUS_BADGE_KEYS[u.status] || STATUS_BADGE_KEYS.available);
                const isSold = u.status === 'sold';
                const isAvailable = u.status === 'available';
                return (
                  <tr
                    key={u.id}
                    className={`border-b border-border transition-colors ${
                      isSold
                        ? 'bg-red-50/60 hover:bg-red-100/60'
                        : 'hover:bg-[var(--surface-1)]'
                    }`}
                    data-testid={`unit-row-${u.code}`}
                  >
                    <td className="px-3 py-2.5 font-mono font-semibold text-[hsl(var(--primary))]">
                      {u.code}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className="text-[10px]">
                        {t('resortDetail.unitsTable.blockLabel', 'Blocco {{name}}', { name: u.block })}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right">{u.mq != null ? `${u.mq} ${t('resortDetail.unitsTable.cols.sqm', 'MQ').toLowerCase()}` : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-serif font-semibold text-[hsl(var(--primary))]">
                      {u.price_total != null ? fmtEUR(u.price_total) : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-muted-foreground hidden md:table-cell">
                      {u.price_per_mq != null ? `€${Number(u.price_per_mq).toLocaleString('it-IT')}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right text-xs text-emerald-700 font-medium hidden lg:table-cell">
                      {u.roi_annual != null ? `+${fmtEUR(u.roi_annual)}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <Badge className={`border text-[10px] ${statusStyle}`}>
                        {statusLabel}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {isAvailable ? (
                        <Button
                          size="sm"
                          onClick={() => openReserveModal(u)}
                          data-testid={`reserve-btn-${u.code}`}
                          className="h-7 text-xs"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {t('resortDetail.unitsTable.actions.info', 'Info')}
                        </Button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground text-xs">
                    {t('resortDetail.unitsTable.emptyFiltered', 'Nessun appartamento con i filtri selezionati.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ★ 2026-08-14 (Fase 51) — Vecchio modal "Richiedi info" DISABILITATO su richiesta utente:
          il nuovo flusso passa da BookSeniorModal (Zoom diretto con Senior/CEO).
          Mantenuto il codice in false && per possibile riabilitazione futura. */}
      {false && (
      <Dialog open={!!selectedUnit} onOpenChange={(v) => !v && setSelectedUnit(null)}>
        <DialogContent className="max-w-lg" data-testid="reserve-modal">
          {submittedOk ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
              <div className="font-serif text-xl text-[hsl(var(--primary))]">Richiesta ricevuta!</div>
              <p className="text-sm text-muted-foreground">
                Grazie! Ti contatteremo entro <b>24 ore</b> con tutte le informazioni,
                planimetrie e conto corrente per il versamento della caparra.
              </p>
              <Button onClick={() => setSelectedUnit(null)}>Chiudi</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-[hsl(var(--primary))]" />
                  Richiedi info · {selectedUnit?.code}
                </DialogTitle>
                <DialogDescription>
                  Compila il form: ti richiameremo entro 24h con brochure completa,
                  planimetria, e istruzioni per la caparra confirmatoria di €5.000.
                </DialogDescription>
              </DialogHeader>

              {selectedUnit && (
                <div className="rounded-lg border border-border bg-[var(--surface-1)] p-3 space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Superficie</span>
                    <span className="font-medium">{selectedUnit.mq} mq · Blocco {selectedUnit.block}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Prezzo totale</span>
                    <span className="font-serif font-semibold text-[hsl(var(--primary))]">
                      {fmtEUR(selectedUnit.price_total)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Anticipo alla firma</span>
                    <span className="font-medium">{fmtEUR(selectedUnit.advance)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">ROI netto annuo</span>
                    <span className="text-emerald-700 font-medium">+{fmtEUR(selectedUnit.roi_annual)}</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Nome e cognome *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Mario Rossi"
                    className="mt-1"
                    data-testid="reserve-name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="mario@email.com"
                      className="mt-1"
                      data-testid="reserve-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Telefono</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+39 340..."
                      className="mt-1"
                      data-testid="reserve-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Messaggio (opzionale)</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Vorrei più info sulle scadenze, disponibilità, visita on-site..."
                    rows={3}
                    className="mt-1"
                    data-testid="reserve-message"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setSelectedUnit(null)} disabled={submitting}>
                  Annulla
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="reserve-submit">
                  <Send className="w-4 h-4 mr-1" />
                  {submitting ? 'Invio…' : 'Invia richiesta'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* ★ 2026-08-14 (Fase 51) — Modal prenota Zoom con Senior/CEO */}
      <BookSeniorModal
        open={bookOpen}
        onOpenChange={setBookOpen}
        slug={slug}
        context={bookContext}
      />
    </div>
  );
}
